import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../services/adminApi';
import './AdminConsole.css';

const formatTime = (value) => {
	if (!value) return '';
	try {
		return new Date(value).toLocaleString('en-IN', {
			day: '2-digit',
			month: 'short',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true
		});
	} catch {
		return String(value);
	}
};

const AdminWhatsAppConsole = () => {
	const navigate = useNavigate();
	const [conversations, setConversations] = useState([]);
	const [selectedPhone, setSelectedPhone] = useState('');
	const [messages, setMessages] = useState([]);
	const [reply, setReply] = useState('');
	const [startForm, setStartForm] = useState({ phone: '', serviceType: '' });
	const [error, setError] = useState('');
	const [info, setInfo] = useState('');
	const [loading, setLoading] = useState(false);
	const [sessionOpen, setSessionOpen] = useState(false);
	const threadEndRef = useRef(null);
	const abortRef = useRef(null);

	const loadConversations = useCallback(async () => {
		try {
			const res = await adminApi.get('/api/v1/admin/whatsapp/conversations');
			const list = Array.isArray(res.data) ? res.data : [];
			setConversations(list);
			if (selectedPhone) {
				const match = list.find(c => c.phone === selectedPhone);
				setSessionOpen(!!match?.sessionOpen);
			}
		} catch (err) {
			console.error(err);
			setError('Failed to load conversations');
		}
	}, [selectedPhone]);

	const loadThread = useCallback(async (phone) => {
		if (!phone) return;
		try {
			const res = await adminApi.get(`/api/v1/admin/whatsapp/conversations/${encodeURIComponent(phone)}`);
			setMessages(Array.isArray(res.data) ? res.data : []);
		} catch (err) {
			console.error(err);
			setError('Failed to load chat thread');
		}
	}, []);

	const selectConversation = async (phone, open) => {
		setSelectedPhone(phone);
		setSessionOpen(!!open);
		setError('');
		setInfo('');
		await loadThread(phone);
	};

	useEffect(() => {
		loadConversations();
	}, [loadConversations]);

	useEffect(() => {
		threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	// Live SSE — pass JWT as query param (reliable behind proxies / EventSource)
	useEffect(() => {
		const token = localStorage.getItem('adminAccessToken');
		if (!token) return undefined;

		const controller = new AbortController();
		abortRef.current = controller;
		let buffer = '';
		let retryTimer = null;

		const connect = async () => {
			try {
				const url = `/api/v1/admin/whatsapp/stream?access_token=${encodeURIComponent(token)}`;
				const res = await fetch(url, {
					headers: {
						Authorization: `Bearer ${token}`,
						Accept: 'text/event-stream'
					},
					signal: controller.signal
				});
				if (res.status === 401 || res.status === 403) {
					console.error('SSE unauthorized', res.status);
					return;
				}
				if (!res.ok || !res.body) {
					throw new Error('SSE connection failed: ' + res.status);
				}
				const reader = res.body.getReader();
				const decoder = new TextDecoder();

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					buffer += decoder.decode(value, { stream: true });
					const parts = buffer.split('\n\n');
					buffer = parts.pop() || '';
					for (const chunk of parts) {
						const lines = chunk.split('\n');
						let eventName = 'message';
						let dataLine = '';
						for (const line of lines) {
							if (line.startsWith('event:')) eventName = line.slice(6).trim();
							if (line.startsWith('data:')) dataLine += line.slice(5).trim();
						}
						if (eventName === 'message' && dataLine) {
							try {
								const evt = JSON.parse(dataLine);
								if (evt.type === 'MESSAGE' && evt.phone) {
									setConversations(prev => {
										const others = prev.filter(c => c.phone !== evt.phone);
										return [{
											phone: evt.phone,
											profileName: evt.profileName,
											lastMessage: evt.messageBody,
											lastDirection: evt.direction,
											lastTimestamp: evt.timestamp,
											sessionOpen: evt.direction === 'INBOUND' ? true : prev.find(c => c.phone === evt.phone)?.sessionOpen
										}, ...others];
									});
									setSelectedPhone(current => {
										if (current === evt.phone) {
											setMessages(prev => {
												if (evt.messageId && prev.some(m => m.id === evt.messageId)) return prev;
												return [...prev, {
													id: evt.messageId,
													contactPhone: evt.phone,
													messageBody: evt.messageBody,
													direction: evt.direction,
													profileName: evt.profileName,
													timestamp: evt.timestamp,
													status: evt.status
												}];
											});
											if (evt.direction === 'INBOUND') setSessionOpen(true);
										}
										return current;
									});
								}
							} catch (parseErr) {
								console.debug('SSE parse skip', parseErr);
							}
						}
					}
				}
				// stream ended — reconnect
				if (!controller.signal.aborted) {
					retryTimer = setTimeout(connect, 3000);
				}
			} catch (err) {
				if (controller.signal.aborted) return;
				console.error('SSE error', err);
				retryTimer = setTimeout(connect, 4000);
			}
		};

		connect();
		return () => {
			controller.abort();
			if (retryTimer) clearTimeout(retryTimer);
		};
	}, []);

	const handleStart = async (e) => {
		e.preventDefault();
		setError('');
		setInfo('');
		setLoading(true);
		try {
			await adminApi.post('/api/v1/admin/whatsapp/start', {
				phone: startForm.phone.trim(),
				serviceType: startForm.serviceType.trim()
			});
			setInfo('Conversation start template sent. Waiting for user reply to open session.');
			const phone = startForm.phone.trim().startsWith('+')
				? startForm.phone.trim()
				: `+${startForm.phone.trim()}`;
			setSelectedPhone(phone);
			setSessionOpen(false);
			await loadConversations();
			await loadThread(phone);
			setStartForm({ phone: '', serviceType: '' });
		} catch (err) {
			setError(err.response?.data?.message || err.message || 'Failed to start conversation');
		} finally {
			setLoading(false);
		}
	};

	const handleSend = async (e) => {
		e.preventDefault();
		if (!selectedPhone || !reply.trim()) return;
		setError('');
		setInfo('');
		setLoading(true);
		try {
			await adminApi.post('/api/v1/admin/whatsapp/send', {
				phone: selectedPhone,
				message: reply.trim()
			});
			setReply('');
			await loadThread(selectedPhone);
			await loadConversations();
		} catch (err) {
			setError(err.response?.data?.message || err.message || 'Failed to send reply');
		} finally {
			setLoading(false);
		}
	};

	const logout = () => {
		localStorage.removeItem('adminAccessToken');
		localStorage.removeItem('adminRefreshToken');
		navigate('/admin-login');
	};

	return (
		<div className="admin-console">
			<header className="admin-header">
				<div>
					<strong>MyBharatService Admin</strong>
					<span className="admin-subtitle">WhatsApp inbox</span>
				</div>
				<button type="button" className="admin-logout" onClick={logout}>Logout</button>
			</header>

			{(error || info) && (
				<div className={`admin-banner ${error ? 'error' : 'info'}`}>
					{error || info}
				</div>
			)}

			<div className="admin-layout">
				<aside className="admin-sidebar">
					<form className="admin-start-form" onSubmit={handleStart}>
						<h3>Start new chat</h3>
						<input
							placeholder="Provider WhatsApp (+91...)"
							value={startForm.phone}
							onChange={e => setStartForm({ ...startForm, phone: e.target.value })}
							required
						/>
						<input
							placeholder="Service type"
							value={startForm.serviceType}
							onChange={e => setStartForm({ ...startForm, serviceType: e.target.value })}
							required
						/>
						<button type="submit" disabled={loading}>
							{loading ? 'Sending...' : 'Start conversation'}
						</button>
					</form>

					<div className="admin-conv-list">
						<h3>Conversations</h3>
						{conversations.length === 0 && <p className="admin-empty">No chats yet.</p>}
						{conversations.map(c => (
							<button
								type="button"
								key={c.phone}
								className={`admin-conv-item ${selectedPhone === c.phone ? 'active' : ''}`}
								onClick={() => selectConversation(c.phone, c.sessionOpen)}
							>
								<div className="admin-conv-top">
									<strong>{c.profileName || c.phone}</strong>
									<span className={`admin-session ${c.sessionOpen ? 'open' : 'closed'}`}>
										{c.sessionOpen ? 'Open' : 'Closed'}
									</span>
								</div>
								{c.profileName && <div className="admin-conv-phone">{c.phone}</div>}
								<div className="admin-conv-preview">{c.lastMessage}</div>
								<div className="admin-conv-time">{formatTime(c.lastTimestamp)}</div>
							</button>
						))}
					</div>
				</aside>

				<main className="admin-thread">
					{!selectedPhone && (
						<div className="admin-empty-thread">
							Select a conversation or start a new chat.
						</div>
					)}
					{selectedPhone && (
						<>
							<div className="admin-thread-header">
								<div>
									<strong>{selectedPhone}</strong>
									<span className={`admin-session ${sessionOpen ? 'open' : 'closed'}`}>
										{sessionOpen ? 'Session open' : 'Session closed — start again to message'}
									</span>
								</div>
							</div>
							<div className="admin-messages">
								{messages.map(m => (
									<div
										key={m.id || `${m.timestamp}-${m.messageBody}`}
										className={`admin-bubble ${m.direction === 'OUTBOUND' ? 'out' : 'in'}`}
									>
										<div className="admin-bubble-body">{m.messageBody}</div>
										<div className="admin-bubble-meta">
											{m.direction === 'INBOUND' ? 'User' : 'Admin'} · {formatTime(m.timestamp)}
										</div>
									</div>
								))}
								<div ref={threadEndRef} />
							</div>
							<form className="admin-reply-form" onSubmit={handleSend}>
								<textarea
									rows={3}
									placeholder={sessionOpen
										? 'Type a reply...'
										: 'Session closed. Use Start conversation (phone + service type) first, then wait for user reply.'}
									value={reply}
									onChange={e => setReply(e.target.value)}
									disabled={!sessionOpen || loading}
									required
								/>
								<button type="submit" disabled={!sessionOpen || loading || !reply.trim()}>
									Send reply
								</button>
							</form>
						</>
					)}
				</main>
			</div>
		</div>
	);
};

export default AdminWhatsAppConsole;
